import { useCallback, useEffect, useMemo } from 'react';
import { useShowDetailsTableData } from './useShowDetailsTableData';
import { useSelectedEpisodeDetails } from './useGraphqlAudioDetails';
import { useGraphqlAudioGenAI } from './useGraphqlAudioGenAI';

import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { AudioDetails, GenAI } from '@insights_module/types/graphql/Schema';
import { removeShowTitleFromEpisodeTitle } from '@utils/ADCUtils';
import {
  PROMOTION_VIEW_ITEMS,
  useEpisodeDetailsContext,
} from '@insights_module/context/EpisodeDetailsContext';
import { useLibraryTableData } from '@insights_module/hooks/useLibraryTableData';

export type PromotionType =
  | 'show_notes'
  | 'summary'
  | 'twitter'
  | 'facebook'
  | 'linkedin'
  | 'instagram'
  | 'article';

export type GenAIFromAudioContentType = {
  id: string;
  type: PromotionType;
  value: string | null;
};

type EpisodesWithRecentlyGeneratedData = {
  [key: string]: string[];
};

export const useEpisodeDetailsData = () => {
  const { selectedTableType, selectedEpisode } = useInsightsContext();
  const {
    activeView,
    selectedSocialPostType,
    selectedPromotionItemIsGenerating,
    setSelectedPromotionItemIsGenerating,
  } = useEpisodeDetailsContext();

  const { data: episodeGenAI } = useGraphqlAudioGenAI({
    audioId: selectedEpisode,
    enabled: selectedPromotionItemIsGenerating,
    refetchInterval: 5000,
  });

  const isEpisodesTable = useMemo(() => selectedTableType === 'episodes', [selectedTableType]);

  const {
    getNextEpisode: getNextEpisodeFromLibraryTable,
    getPrevEpisode: getPrevEpisodeFromLibraryTable,
    getEpisodeById: getEpisodeByIdFromLibraryTable,
    getEpisodeIndex: getEpisodeIndexFromLibraryTable,
    getGarmForEpisode: getGarmForEpisodeFromLibraryTable,
    totalAudiosCount: episodesTotalCountFromLibraryTable,
    audiosCurrentPage: audiosCurrentPageFromLibraryTable,
  } = useLibraryTableData();

  const getTotalDataEpisodeIndex = useCallback(
    (id: string) => {
      const index = getEpisodeIndexFromLibraryTable(id);
      return (audiosCurrentPageFromLibraryTable - 1) * 20 + index;
    },
    [audiosCurrentPageFromLibraryTable, getEpisodeIndexFromLibraryTable]
  );

  const {
    getNextEpisode: getNextEpisodeFromShowDetails,
    getPrevEpisode: getPrevEpisodeFromShowDetails,
    getEpisodeById: getEpisodeByIdFromShowDetails,
    getEpisodeIndex: getEpisodeIndexFromShowDetails,
    episodesTotalCount: episodesTotalCountFromShowDetails,
    getGarmByEpisodeId: getGarmByEpisodeIdFromShowDetails,
  } = useShowDetailsTableData();

  const getEpisodeById = useMemo(
    () => (isEpisodesTable ? getEpisodeByIdFromLibraryTable : getEpisodeByIdFromShowDetails),
    [isEpisodesTable, getEpisodeByIdFromLibraryTable, getEpisodeByIdFromShowDetails]
  );

  const getPrevEpisode = useMemo(
    () => (isEpisodesTable ? getPrevEpisodeFromLibraryTable : getPrevEpisodeFromShowDetails),
    [isEpisodesTable, getPrevEpisodeFromLibraryTable, getPrevEpisodeFromShowDetails]
  );

  const getNextEpisode = useMemo(
    () => (isEpisodesTable ? getNextEpisodeFromLibraryTable : getNextEpisodeFromShowDetails),
    [isEpisodesTable, getNextEpisodeFromLibraryTable, getNextEpisodeFromShowDetails]
  );

  const getEpisodeIndex = useMemo(
    () => (isEpisodesTable ? getTotalDataEpisodeIndex : getEpisodeIndexFromShowDetails),
    [isEpisodesTable, getEpisodeIndexFromShowDetails, getTotalDataEpisodeIndex]
  );

  const episodesTotalCount = useMemo(
    () =>
      isEpisodesTable ? episodesTotalCountFromLibraryTable : episodesTotalCountFromShowDetails,
    [isEpisodesTable, episodesTotalCountFromLibraryTable, episodesTotalCountFromShowDetails]
  );

  const getGarmForEpisode = useMemo(
    () => (isEpisodesTable ? getGarmForEpisodeFromLibraryTable : getGarmByEpisodeIdFromShowDetails),
    [isEpisodesTable, getGarmForEpisodeFromLibraryTable, getGarmByEpisodeIdFromShowDetails]
  );

  const { data, isLoading } = useSelectedEpisodeDetails();

  const selectedEpisodeDetails: (AudioDetails & { showTile: string; coverImage: string }) | null =
    useMemo(() => {
      if (!data) {
        return null;
      }

      const show = data?.groups?.find((group) => group.type === 'show');
      if (!show) {
        return {
          ...data,
          showTile: '',
          coverImage: '',
        };
      }

      return {
        ...data,
        title: removeShowTitleFromEpisodeTitle(data.title, show.name),
        showTile: show.name,
        coverImage: show.thumbnail_url,
      } as AudioDetails & { showTile: string; coverImage: string };
    }, [data]);

  const selectedPromotionItem = useMemo(() => {
    if (
      !selectedEpisodeDetails?.id ||
      !selectedEpisodeDetails?.gen_ai ||
      !PROMOTION_VIEW_ITEMS.includes(activeView) ||
      !selectedSocialPostType
    )
      return null;

    const key =
      activeView === 'social_posts' ? selectedSocialPostType : (activeView as keyof GenAI);

    return {
      id: selectedEpisodeDetails.id,
      type: key,
      value: selectedEpisodeDetails.gen_ai[key],
    } as GenAIFromAudioContentType;
  }, [
    selectedEpisodeDetails?.id,
    selectedEpisodeDetails?.gen_ai,
    activeView,
    selectedSocialPostType,
    PROMOTION_VIEW_ITEMS,
  ]);

  function getEpisodesWithRecentlyGeneratedData(): EpisodesWithRecentlyGeneratedData {
    const localEpisodes = localStorage.getItem('episodes-with-recently-generated-data');
    // if the local storage is an array, it means it's the old format and we need to remove it
    if (localEpisodes && Array.isArray(JSON.parse(localEpisodes))) {
      localStorage.removeItem('episodes-with-recently-generated-data');
    }

    return localEpisodes ? JSON.parse(localEpisodes) : {};
  }

  function addEpisodeToEpisodesWithRecentlyGeneratedData(
    episodeId: string,
    generatedTypes: string[]
  ) {
    const episodesWithRecentlyGeneratedData = getEpisodesWithRecentlyGeneratedData();
    const selectedEpisodePersistedData = episodesWithRecentlyGeneratedData[episodeId] || [];
    const updatedEpisodes = {
      ...episodesWithRecentlyGeneratedData,
      [episodeId]: [...selectedEpisodePersistedData, ...generatedTypes],
    };

    localStorage.setItem('episodes-with-recently-generated-data', JSON.stringify(updatedEpisodes));
    setSelectedPromotionItemIsGenerating(true);
  }

  function removeGeneratedTypesFromEpisodesWithRecentlyGeneratedData(
    episodeId: string,
    generatedTypes: string[]
  ) {
    const episodesWithRecentlyGeneratedData = getEpisodesWithRecentlyGeneratedData();
    const episodesToUpdate = { ...episodesWithRecentlyGeneratedData };
    const selectedEpisodePersistedData = episodesToUpdate[episodeId] || [];
    const filteredEpisodeData = selectedEpisodePersistedData.filter(
      (type) => !generatedTypes.includes(type)
    );
    if (!filteredEpisodeData.length) {
      delete episodesToUpdate[episodeId];
    } else {
      episodesToUpdate[episodeId] = filteredEpisodeData;
    }

    localStorage.setItem('episodes-with-recently-generated-data', JSON.stringify(episodesToUpdate));
  }

  useEffect(() => {
    const episodesWithRecentlyGeneratedData = getEpisodesWithRecentlyGeneratedData();
    if (
      selectedEpisodeDetails?.id &&
      episodesWithRecentlyGeneratedData[selectedEpisodeDetails.id]
    ) {
      const newGeneratedTypes = Object.entries(selectedEpisodeDetails?.gen_ai || {}).reduce(
        (acc, [key, value]) => {
          if (value) {
            return [...acc, key];
          }
          return acc;
        },
        [] as string[]
      );

      removeGeneratedTypesFromEpisodesWithRecentlyGeneratedData(
        selectedEpisodeDetails.id,
        newGeneratedTypes
      );
    }
  }, [selectedEpisodeDetails?.id, selectedEpisodeDetails?.gen_ai]);

  useEffect(() => {
    if (selectedPromotionItem) {
      const episodesWithRecentlyGeneratedData = getEpisodesWithRecentlyGeneratedData();
      const isGenerating =
        !selectedPromotionItem.value &&
        episodesWithRecentlyGeneratedData[selectedPromotionItem.id]?.includes(
          selectedPromotionItem.type
        );
      setSelectedPromotionItemIsGenerating(!!isGenerating);
    } else {
      setSelectedPromotionItemIsGenerating(false);
    }
  }, [selectedPromotionItem, selectedPromotionItemIsGenerating]);

  useEffect(() => {
    if (selectedPromotionItem?.type && episodeGenAI?.[selectedPromotionItem.type]) {
      selectedPromotionItem.value = episodeGenAI[selectedPromotionItem.type];
      setSelectedPromotionItemIsGenerating(false);
    }
  }, [episodeGenAI, selectedPromotionItem]);

  return {
    getPrevEpisode,
    getNextEpisode,
    getEpisodeById,
    getEpisodeIndex,
    getGarmForEpisode,
    episodesTotalCount,
    selectedEpisodeDetails,
    selectedPromotionItem,
    isLoading,
    addEpisodeToEpisodesWithRecentlyGeneratedData,
    removeGeneratedTypesFromEpisodesWithRecentlyGeneratedData,
    selectedPromotionItemIsGenerating,
  };
};
